import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api, InsurancePolicy, Application, Notification } from '../../lib/api';
import { ProfileCompletion } from './ProfileCompletion';
import {
  CheckCircle2,
  AlertCircle,
  Briefcase,
  DollarSign,
  Upload,
  Search,
  TrendingUp,
  Clock,
  Shield
} from 'lucide-react';

interface ContractorDashboardProps {
  onViewChange?: (view: string) => void;
}

export const ContractorDashboard: React.FC<ContractorDashboardProps> = ({ onViewChange }) => {
  const { profile } = useAuth();
  const [stats, setStats] = useState({
    activeJobs: 0,
    earnings: 0,
    applications: 0,
  });
  const [insurance, setInsurance] = useState<InsurancePolicy[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, [profile]);

  const loadDashboardData = async () => {
    if (!profile) return;

    try {
      const [applications, insurance, notifications] = await Promise.all([
        api.get<Application[]>('/applications', { contractor_id: profile.id, status: ['contacted', 'verified', 'hired'] }),
        api.get<InsurancePolicy[]>('/insurance_policies', { user_id: profile.id }),
        api.get<Notification[]>('/notifications', { user_id: profile.id, read: false, limit: 5 })
      ]);

      const applicationsList = applications || [];
      const activeJobs = applicationsList.filter(a => a.status === 'hired').length;

      setStats({
        activeJobs,
        earnings: activeJobs * 850,
        applications: applicationsList.length,
      });

      setInsurance(insurance || []);
      setNotifications(notifications || []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'pending':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      default:
        return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  const getInsuranceStatus = (policy: InsurancePolicy) => {
    const daysUntilExpiry = Math.ceil(
      (new Date(policy.expiry_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiry < 0) return { text: 'Expired', color: 'text-red-600 bg-red-50' };
    if (daysUntilExpiry < 30) return { text: `${daysUntilExpiry} days left`, color: 'text-amber-600 bg-amber-50' };
    return { text: 'Active', color: 'text-green-600 bg-green-50' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (showProfileCompletion) {
    return <ProfileCompletion onBack={() => setShowProfileCompletion(false)} />;
  }

  return (
    <div className="space-y-6">
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 rounded-2xl p-8 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="relative">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Welcome back, {profile?.full_name}</h1>
            <p className="text-slate-300">Here's what's happening with your work today</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
              <div className="text-xs text-slate-300 mb-1">Active Jobs</div>
              <div className="text-2xl font-bold text-white">{stats.activeJobs}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
              <div className="text-xs text-slate-300 mb-1">Earnings</div>
              <div className="text-2xl font-bold text-white">${stats.earnings.toLocaleString()}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
              <div className="text-xs text-slate-300 mb-1">Applications</div>
              <div className="text-2xl font-bold text-white">{stats.applications}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
              <div className="text-xs text-slate-300 mb-1">Policies</div>
              <div className="text-2xl font-bold text-white">{insurance.filter(i => i.status === 'active').length}</div>
            </div>
          </div>
        </div>
      </div>

      <div className={`profile-completion border-2 rounded-xl p-6 shadow-sm hover:shadow-md transition-all ${getStatusColor(profile?.verification_status || 'unverified')}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            {profile?.verification_status === 'verified' ? (
              <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <h3 className="font-semibold text-lg">
                {profile?.verification_status === 'verified'
                  ? 'Verified Profile'
                  : profile?.verification_status === 'pending'
                  ? 'Verification Pending'
                  : 'Complete Your Profile'}
              </h3>
              <p className="text-sm mt-1 opacity-90">
                {profile?.verification_status === 'verified'
                  ? 'Your profile is verified and visible to all GCs'
                  : profile?.verification_status === 'pending'
                  ? 'Your documents are being reviewed'
                  : 'Get verified to unlock all jobs and increase your visibility'}
              </p>
            </div>
          </div>
          {profile?.verification_status !== 'verified' && (
            <button
              onClick={() => setShowProfileCompletion(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg"
            >
              Complete
            </button>
          )}
        </div>
      </div>

      <div className="stats-overview grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl border border-blue-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">{stats.activeJobs}</div>
          <div className="text-sm text-slate-600 font-medium">Active Jobs</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full" style={{width: '70%'}}></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-green-50 rounded-xl border border-green-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-md">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">${stats.earnings.toLocaleString()}</div>
          <div className="text-sm text-slate-600 font-medium">This Month</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full" style={{width: '85%'}}></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl border border-blue-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
              <Search className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">{stats.applications}</div>
          <div className="text-sm text-slate-600 font-medium">Applications</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full" style={{width: '60%'}}></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center shadow-md">
              <Shield className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">
            {insurance.filter(i => i.status === 'active').length}
          </div>
          <div className="text-sm text-slate-600 font-medium">Active Policies</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-slate-600 to-slate-700 rounded-full" style={{width: '100%'}}></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-xl font-bold text-slate-900">Quick Actions</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 quick-actions">
            <button
              onClick={() => onViewChange?.('profile')}
              className="bg-gradient-to-br from-white to-blue-50 border-2 border-blue-200 hover:border-blue-500 hover:shadow-xl rounded-xl p-6 text-left transition-all group hover:scale-[1.02]"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md">
                <Upload className="w-5 h-5 text-white" />
              </div>
              <div className="font-bold text-slate-900">Upload Docs</div>
              <div className="text-sm text-slate-600 mt-1">Add licenses & certs</div>
            </button>

            <button
              onClick={() => onViewChange?.('jobs')}
              className="bg-gradient-to-br from-white to-blue-50 border-2 border-blue-200 hover:border-blue-500 hover:shadow-xl rounded-xl p-6 text-left transition-all group hover:scale-[1.02]"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div className="font-bold text-slate-900">Apply for Job</div>
              <div className="text-sm text-slate-600 mt-1">Browse open positions</div>
            </button>

            <button
              onClick={() => onViewChange?.('insurance')}
              className="bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-500 hover:shadow-xl rounded-xl p-6 text-left transition-all group hover:scale-[1.02]"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div className="font-bold text-slate-900">Get Quotes</div>
              <div className="text-sm text-slate-600 mt-1">Compare insurance</div>
            </button>
          </div>

          {insurance.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="font-bold text-slate-900 mb-4 flex items-center">
                <Shield className="w-5 h-5 mr-2 text-blue-600" />
                Insurance Status
              </h3>
              <div className="space-y-3">
                {insurance.slice(0, 3).map((policy) => {
                  const status = getInsuranceStatus(policy);
                  return (
                    <div key={policy.id} className="flex items-center justify-between pb-3 border-b border-slate-100 last:border-0">
                      <div>
                        <div className="font-medium text-slate-900 capitalize">
                          {policy.policy_type.replace('_', ' ')}
                        </div>
                        <div className="text-sm text-slate-600">{policy.provider}</div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                        {status.text}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-blue-600" />
              Recent Activity
            </h3>
          </div>

          {notifications.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p className="text-sm">No new notifications</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notif) => (
                <div key={notif.id} className="pb-3 border-b border-slate-100 last:border-0">
                  <div className="font-medium text-sm text-slate-900">{notif.title}</div>
                  <div className="text-xs text-slate-600 mt-1">{notif.message}</div>
                  <div className="text-xs text-slate-400 mt-1">
                    {new Date(notif.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
